export type Pickup = 'FACTORY' | 'WAREHOUSE';
export const PICKUP_LABEL: Record<Pickup, string> = { FACTORY: 'Factory', WAREHOUSE: 'Warehouse' };
export const SHORT_CODE_LABEL = {
  A: 'Physical qty not available', B: 'Space not available in truck', C: 'Damage during loading', D: 'Material not packed', E: 'Other (text required)',
} as const;

export interface PendingLine {
  salesOrderItemId: string; productId: string | null; description: string; unit: string; orderedQty: number; dispatchedQty: number;
  onOpenFormsQty: number; pendingQty: number; serialised: boolean;
  pool: Array<{ id: string; qty: number; shortCode: string; reason: string; dispatchRef: string }>;
  candidates: Array<{ code: string; locationCode: string | null; qualityGrade: string | null; state: string; packed: boolean }>;
  blockReason: string | null;
}
export interface PendingSoForForm { salesOrder: { id: string; number: string; status: string; customerName: string }; lines: PendingLine[] }

export interface DispatchFormDetail {
  assignedLoaderId: string | null; damageReports: Array<{ code: string; note: string; by: string }>;
  id: string; number: string; status: string; vehicleNo: string; driverName: string | null; driverPhone: string | null; transporterName: string | null; distanceKm: number | null;
  builtByName: string; submittedAt: string;
  approval: { status: 'waiting' | 'held' | 'approved' | 'overridden' | 'cancelled'; byName: string | null; at: string | null; holdReason: string | null; overrideReason: string | null; note: string | null };
  orders: Array<{ salesOrderId: string; number: string; partyName: string }>;
  lines: Array<{ id: string; salesOrderId: string; salesOrderNumber: string; salesOrderItemId: string; description: string; pickupLocation: Pickup; serialised: boolean; plannedQty: number; countedQty: number | null; countedByName: string | null; loadedQty: number; shorts: Array<{ id: string; qty: number; code: string; reason: string; note: string | null; unitCode: string | null }> }>;
  units: Array<{ id: string; lineId: string; code: string; productName: string; salesOrderNumber: string; pickupLocation: Pickup; grade: string | null; packed: boolean; loadStatus: 'pending' | 'loaded' | 'short'; lastScan: { result: 'loaded' | 'refused'; message: string; at: string } | null }>;
  photos: Record<Pickup, { empty: string | null; layer1: string | null; full: string | null; strapped: string | null } | null>;
  confirm: { canConfirm: boolean; blockers: string[]; loadedUnits: number; countedItems: number };
  result: ConfirmLoadResult | null;
}
export interface DispatchFormRow { assignedLoaderId: string | null; customerNames: string[]; shortCount: number; countPending: number; confirmedAt: string | null; id: string; number: string; status: string; vehicleNo: string; orders: string[]; builtByName: string; plannedUnits: number; loadedUnits: number; submittedAt: string }

export const STATUS_PILL: Record<string, { label: string; className: string }> = {
  awaiting_approval: { label: 'Waiting for accounts', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' },
  held: { label: 'Held', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  approved: { label: 'Accounts approved', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  loading: { label: 'Loading', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  confirming: { label: 'Confirming', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  confirmed: { label: 'Load confirmed', className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' },
};

export function apiError(err: unknown, fallback: string): string {
  return (err as any)?.response?.data?.error ?? fallback;
}

export type ApprovalTab = 'waiting' | 'held' | 'approved_today';
export interface OrderMoney {
  salesOrderId: string;
  number: string;
  partyName: string;
  partyKind: 'dealer' | 'customer';
  units: number;
  valueLeavingPaise: number;
  summary: { stale: boolean } | null;
  decision: { state: string; message: string } | null;
  syncAgeLabel: string;
  readError: string | null;
}
export interface ApprovalCard {
  id: string;
  number: string;
  vehicleNo: string;
  builtByName: string;
  submittedAt: string;
  status: string;
  holdReason: string | null;
  heldByName: string | null;
  orders: OrderMoney[];
}
export interface ApprovalQueue {
  counts: {
    waiting: number;
    held: number;
    approvedToday: number;
  };
  cards: ApprovalCard[];
}

export interface ConfirmLoadResult {
  formId: string;
  number: string;
  status: 'confirmed';
  invoices: Array<{
    salesOrderId: string;
    invoiceId: string;
    number: string;
    totalPaise: number;
    amountAApplied: boolean;
    shipmentId: string;
    irn: string;
    ewayBill: string;
    tally: string;
  }>;
  gatePass: { id: string; number: string } | null;
  gatePassNote: string | null;
  warranties: {
    created: number;
    skippedNoYears: string[];
  };
  pool: Array<{
    salesOrderItemId: string;
    dispatchRef: string;
    qty: number;
    code: string;
    poolEntryId: string | null;
  }>;
  consequences: {
    stockChecks: string[];
    dispositions: string[];
    packingSignals: string[];
    problems: string[];
  };
}
